import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiAuthExchange, getAnonSessionId } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { finalizeLogin } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("No session token returned. Please try signing in again.");
      return;
    }
    const emergentSessionId = decodeURIComponent(m[1]);
    const anon = getAnonSessionId();

    apiAuthExchange(emergentSessionId, anon)
      .then((data) => {
        finalizeLogin(data);
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true, state: { user: data.user } });
      })
      .catch(() => {
        setError("Authentication failed. Please try again.");
      });
  }, []); // eslint-disable-line

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "#F8FAFC" }}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-[#E9ECEF] bg-white px-6 py-8 sm:px-8 sm:py-10 text-center shadow-[0_12px_40px_rgba(15,23,42,0.08)]"
        data-testid="auth-callback"
      >
        <motion.div
          animate={error ? { rotate: 0 } : { rotate: 360 }}
          transition={error ? { duration: 0 } : { duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 rounded-2xl bg-[#0F172A] flex items-center justify-center mx-auto mb-4"
        >
          <Sparkles size={22} className="text-white" />
        </motion.div>
        {error ? (
          <>
            <div className="font-display text-xl font-semibold text-[#0F172A] mb-2">
              Sign-in failed
            </div>
            <div className="text-[#475569] text-sm mb-5 leading-relaxed">{error}</div>
            <button
              data-testid="auth-callback-retry"
              onClick={() => navigate("/", { replace: true })}
              className="w-full sm:w-auto px-5 py-2.5 min-h-[44px] rounded-full bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B] transition-colors"
            >
              Back to app
            </button>
          </>
        ) : (
          <>
            <div className="font-display text-xl font-semibold text-[#0F172A] mb-1">
              Signing you in…
            </div>
            <div className="text-[#64748B] text-sm font-mono-num">
              Linking your vault and credits
            </div>
          </>
        )}
      </div>
    </div>
  );
}
